import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MapPin, ChevronRight, Heart } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { useLead } from "@/components/public/LeadDialog";
import { memberApi, getMemberToken } from "@/lib/member";
import LocationsSections, { withLocationDefaults } from "@/components/public/LocationsSections";
import RelatedServices from "@/components/public/RelatedServices";
import { Picture } from "@/components/public/Picture";

function Breadcrumbs({ items }) {
  return (
    <nav className="container-cz pt-6 flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
          {it.to ? <Link to={it.to} className="hover:text-primary">{it.label}</Link> : <span className="text-slate-900 font-medium">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

function PageHead({ eyebrow, title, subtitle }) {
  return (
    <div className="container-cz pt-8 pb-10">
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 max-w-3xl">{title}</h1>
      {subtitle && <p className="text-lg text-slate-600 mt-4 max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function useSaved() {
  const [slugs, setSlugs] = useState(() => new Set());
  const navigate = useNavigate();
  useEffect(() => {
    if (!getMemberToken()) return;
    memberApi.me()
      .then((r) => setSlugs(new Set((r.data.saved_locations || []).map((s) => s.slug || s))))
      .catch(() => {});
  }, []);
  const toggle = async (payload) => {
    if (!getMemberToken()) {
      toast.info("Sign in to save your favourites");
      navigate("/account");
      return;
    }
    try {
      const { data } = await memberApi.toggleSaved(payload);
      const next = new Set((data.saved_locations || []).map((s) => s.slug || s));
      setSlugs(next);
      toast.success(next.has(payload.slug) ? "Saved to your favourites" : "Removed from favourites");
    } catch {
      toast.error("Could not update favourites");
    }
  };
  return { slugs, toggle };
}

function SaveHeart({ saved, onClick, testid }) {
  return (
    <span
      role="button"
      tabIndex={0}
      data-testid={testid}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onClick(); } }}
      aria-label={saved ? "Remove from favourites" : "Save to favourites"}
      className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform cursor-pointer"
    >
      <Heart className={`w-[18px] h-[18px] ${saved ? "fill-rose-500 text-rose-500" : "text-slate-600"}`} />
    </span>
  );
}

export function WorkspacesList() {
  useSeo("/office-space");
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  const navigate = useNavigate();
  const { slugs, toggle } = useSaved();
  useEffect(() => {
    api.get("/public/workspaces").then((r) => setItems(r.data));
    api.get("/public/homepage").then((r) => setSettings(r.data.settings));
  }, []);
  return (
    <div className="bg-white min-h-screen">
      <Header phone={settings.phone} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Workspaces" }]} />
      <PageHead eyebrow="Our Workspaces" title="Flexible Space Solutions Tailored to Suit You"
        subtitle="From private offices to fully bespoke managed spaces, find the workspace that fits how your team works best." />
      <div className="container-cz pb-20 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((w) => (
          <button key={w.id} onClick={() => navigate(`/workspaces/${w.slug}`)} data-testid={`ws-list-${w.slug}`}
            className="group card-cz overflow-hidden text-left hover:shadow-cz transition-all hover:-translate-y-1 relative">
            <SaveHeart testid={`ws-save-${w.slug}`} saved={slugs.has(w.slug)}
              onClick={() => toggle({ slug: w.slug, name: w.title, kind: "workspace" })} />
            <div className="aspect-[16/10] overflow-hidden">
              <Picture src={w.image} alt={w.title} loading="lazy" width={700} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-5">
              <h2 className="font-display text-xl font-bold text-slate-900">{w.title}</h2>
              <p className="text-sm text-slate-500 mt-2">{w.description}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-4">Explore <ArrowRight className="w-4 h-4" /></span>
            </div>
          </button>
        ))}
      </div>
      <Footer settings={settings} />
    </div>
  );
}

export function LocationsList() {
  useSeo("/locations");
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/public/page-content", { params: { path: "/locations" } })
      .then((r) => setContent(withLocationDefaults(r.data)))
      .catch(() => setContent(withLocationDefaults({})));
    api.get("/public/homepage").then((r) => setSettings(r.data.settings)).catch(() => {});
  }, []);
  return (
    <div className="bg-white min-h-screen">
      <Header phone={settings.phone} />
      {content && <LocationsSections data={content} />}
      <RelatedServices />
      <Footer settings={settings} />
    </div>
  );
}
